import Joi from "joi";
import { useState, useEffect } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import {
  getColleges,
  getUserTypes,
  getUserPageBlocks,
} from "../../../../../action/common/commonActionCreator";
import { createWidget } from "../../../../../action/masters/widgetActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import MultipleSelect from "../../../../../common-components/multipleSelect";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  ICreateWidget,
  IWidgetData,
} from "../../../../../models/master/widgets";
import { UserTypes } from "../../../../../models/dashboard/adminDashboard";
import LoaderSpinner from "../../../../../common-components/loader";
import { masterType } from "../../../../../constants/masterTypes";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { setPageBlocks } from "../../../../../store/slices/common/commonSlice";
import {
  maxImageSize,
  urlRegex,
} from "../../../../../constants/commonConstants";

const WidgetCreate = (props: ICreateWidget) => {
  const {
    createWidget,
    setShowCreate,
    getColleges,
    colleges,
    pageBlocks,
    getUserTypes,
    userTypes,
    getAllWidgets,
    getUserPageBlocks,
  } = props;

  const initialData: IWidgetData = {
    id: null,
    userType: null,
    name: "",
    file: null,
    description: "",
    url: "",
    sso: "",
    isActive: true,
    pageBlockIds: [],
    collegeIds: [],
  };

  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    const _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "userType") {
      getUserPageBlocks(Number(event.target.value), masterType.widget);
      _data.pageBlockIds = [];
    }
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/ico",
        "image/svg+xml",
        "image/x-icon",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an icon.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  const schema = Joi.object().keys({
    name: Joi.string().required().label("Name is required"),
    userType: Joi.number().greater(0).required().label("Select User Type"),
    file: Joi.object().required().label("File is required"),
    description: Joi.string().required().label("Description is required"),
    url: Joi.string().required().regex(urlRegex).label("Enter Valid URL"),
    pageBlockIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select Page Block"),
    collegeIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select University Management"),
  });

  const ValidationData = {
    ...data,
  };

  const onSave = async () => {
    const validationErrors = validateFields(ValidationData, schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("userType", data.userType?.toString() || "");
      formData.append("name", data.name);
      formData.append("file", data.file);
      formData.append("description", data.description.replace(/\s+$/g, ""));
      formData.append("url", data.url);
      formData.append("isActive", data.isActive.toString());
      data.pageBlockIds?.map((item, index) => {
        formData.append(`pageBlockIds[${index}]`, item.toString());
      });
      data.collegeIds?.map((item, index) => {
        formData.append(`collegeIds[${index}]`, item.toString());
      });
      setLoading(true);
      setIsDisabled(true);
      try {
        await createWidget(formData, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  const setAlert = (type: string, name?: string) => {
    let alertData = getAlertData(type, name);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllWidgets();
      setShowCreate(false);
    }
  };

  useEffect(() => {
    getColleges();
    getUserTypes();
    return () => {
      dispatch(setPageBlocks([]));
    };
  }, []);

  useEffect(() => {
    if (colleges && colleges.length > 0) {
      setData((prevData) => ({
        ...prevData,
        collegeIds: colleges.map((college) => college.id),
      }));
    }
  }, [colleges]);

  return (
    <>
      <div className="modal-header">
        <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
          Add New Widget
        </h1>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          onClick={() => setShowCreate(false)}
        ></button>
      </div>
      <div className="modal-body">
        <div className="row">
          <div className="col-md-12 form-group">
            <FloatingLabelDropDown
              label={"User Type "}
              onChange={onChange}
              isMandatory={true}
              value={data.userType}
              name={"userType"}
              optionsList={sortAlphabetically(
                userTypes?.filter((type) => type.id !== UserTypes.Freshman)
              )}
            />
            {errors?.userType && (
              <div className="text-danger">{errors.userType}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Name "}
              onChange={onChange}
              isMandatory={true}
              value={data.name}
              name={"name"}
              maxLength={50}
            />
            {errors?.name && <div className="text-danger">{errors.name}</div>}
          </div>
          <div className="col-md-6 form-group">
            <div className="d-flex">
              <label className="profile_details_text">
                Icon<i className="label-asterisk"> * </i>
              </label>
              <p className="ms-2 label-info">
                <i className="fa-solid fa-circle-info"></i>
                <span>
                  Upload an icon (SVG, PNG, or ICO). For best results, use a
                  square shape with a transparent background.
                </span>
              </p>
            </div>
            <div className="inputfile-flex">
              {imagePreview && (
                <div className="inputfile-img">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ width: "50px", height: "50px" }}
                  />
                </div>
              )}
              <div className="inputfile-wrap">
                <input
                  type="file"
                  name="file"
                  id="image"
                  className="form-control d-none"
                  required
                  onChange={onFileChange}
                  accept="image/png, image/svg+xml, image/x-icon, image/ico, .svg, .png, .ico,"
                  value={errors?.file && ""}
                />
                <div className="inputfile">
                  <label htmlFor="image">Choose File</label>
                  <span>
                    {imageName
                      ? imageName.trim().split("/").pop()
                      : "No File Choosen"}
                  </span>
                </div>
              </div>
            </div>
            {errors?.file && <div className="text-danger">{errors.file}</div>}
          </div>
          <div className="col-md-12 form-group">
            <label className="profile_details_text">
              Description<i className="label-asterisk"> * </i>
            </label>
            <textarea
              name="description"
              value={data.description}
              onChange={onChange}
              maxLength={1000}
            ></textarea>
            {errors?.description && (
              <div className="text-danger">{errors.description}</div>
            )}
          </div>

          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"URL "}
              onChange={onChange}
              isMandatory={true}
              value={data.url}
              name={"url"}
              maxLength={500}
            />
            {errors?.url && <div className="text-danger">{errors.url}</div>}
          </div>
          <div className="col-md-6 form-group">
            <MultipleSelect
              label={"Page-Block "}
              onChange={onChange}
              isMandatory={true}
              values={data.pageBlockIds as number[]}
              name={"pageBlockIds"}
              optionList={sortAlphabetically(pageBlocks)}
            />
            {errors?.pageBlockIds && (
              <div className="text-danger">{errors.pageBlockIds}</div>
            )}
          </div>
          <div className="col-md-12 form-group">
            <MultipleSelect
              label={"University Management"}
              onChange={onChange}
              isMandatory={true}
              values={data.collegeIds as number[]}
              name={"collegeIds"}
              optionList={sortAlphabetically(colleges)}
            />
            {errors?.collegeIds && (
              <div className="text-danger">{errors.collegeIds}</div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={isDisabled}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            data-bs-dismiss="modal"
            onClick={() => setShowCreate(false)}
            disabled={isDisabled}
          >
            Close
          </button>
        </div>
      </div>

      <SweetAlert
        show={showAlert}
        success={false}
        type={alertType}
        title={alertTitle}
        onConfirm={() => onConfirm()}
        showCancel={showAlertCancel}
        onCancel={() => {
          setShowAlert(false);
          setShowAlertCancel(false);
        }}
        cancelBtnText={"No"}
        confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
      ></SweetAlert>
      {loading && <LoaderSpinner />}
    </>
  );
};

const mapDispatchToProps = {
  createWidget: (data: any, callBack: Function) => createWidget(data, callBack),
  getColleges: () => getColleges(),
  getUserTypes: () => getUserTypes(),
  getUserPageBlocks: (userType: number, masterType: string) =>
    getUserPageBlocks(userType, masterType),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    pageBlocks: state.common.pageBlocks,
    userTypes: state.common.userTypes,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(WidgetCreate);
